import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { TbUserHeart, TbUserEdit, TbLayoutNavbarExpandFilled } from 'react-icons/tb';
import { BiSolidBuildingHouse } from 'react-icons/bi';
import { RiUserAddLine } from 'react-icons/ri';
import { MdDomainAdd } from 'react-icons/md';
import { FiLogOut } from 'react-icons/fi';
import { getAuth, signOut } from 'firebase/auth';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../../firebaseConfig';
import './UserPanelSidebar.css';

const UserPanelSidebar = () => {
    const [entityName, setEntityName] = useState('');
    const [activePage, setActivePage] = useState('AccountDetails');
    const [showAdminOptions, setShowAdminOptions] = useState(false);
    const [isLoading, setIsLoading] = useState(true); // Loading state for sidebar content
    const [isSidebarOpen, setIsSidebarOpen] = useState(false); // State to control sidebar visibility on small screens
    const navigate = useNavigate();
    const location = useLocation();
    const auth = getAuth();

    useEffect(() => {
        // Determine the current active page based on the route path
        const currentPath = location.pathname;
        if (currentPath.includes('AccountDetails')) setActivePage('AccountDetails');
        else if (currentPath.includes('EntityDetails')) setActivePage('EntityDetails');
        else if (currentPath.includes('AddNewAccount')) setActivePage('AddNewAccount');
        else if (currentPath.includes('AddNewEntity')) setActivePage('AddNewEntity');

        // Fetch user's related entity name and check if they have admin privileges
        const fetchEntityInfo = async () => {
            if (auth.currentUser) {
                const uid = auth.currentUser.uid;
                const userDoc = await getDoc(doc(db, 'users', uid));
                if (userDoc.exists()) {
                    const { related_entity_number } = userDoc.data();
                    const entityDoc = await getDoc(doc(db, 'entities', related_entity_number.toString()));
                    if (entityDoc.exists()) {
                        const entityData = entityDoc.data();
                        setEntityName(entityData.entity_name);

                        // Show admin options if entity_type is 'SimpliCITYAdmin'
                        setShowAdminOptions(entityData.entity_name === 'SimpliCITY');
                    }
                }
            }
            setIsLoading(false); // Data is fully loaded
        };

        fetchEntityInfo();
    }, [location, auth.currentUser]);

    // Handle navigation and logout
    const handleNavigation = (page) => {
        setActivePage(page);
        if (page === 'EntityDetails' && showAdminOptions) {
            navigate('/EntityDetailsAdmin'); // Navigate to EntityDetailsAdmin if user is SimpliCITYAdmin
        } else {
            navigate(`/${page}`);
        }
        setIsSidebarOpen(false); // Close sidebar on navigation
    };

    const handleLogout = async () => {
        await signOut(auth);
        navigate('/login');
        setIsSidebarOpen(false); // Close sidebar on logout
    };

    // Toggle sidebar visibility on small screens
    const toggleSidebar = () => {
        setIsSidebarOpen(!isSidebarOpen);
    };

    return (
        <div className="user-panel-sidebar-container">
            {/* Sidebar Toggle Button - Visible on small screens */}
            <button className="sidebar-toggle-button" onClick={toggleSidebar}>
                <TbLayoutNavbarExpandFilled />
            </button>

            {/* Sidebar - Show or hide based on isSidebarOpen */}
            <div className={`user-panel-sidebar ${isSidebarOpen ? 'open' : ''}`}>
                {isLoading ? (
                    // Placeholder for loading state: White background with border only
                    <div className="sidebar-placeholder"></div>
                ) : (
                    // Actual sidebar content when data is loaded
                    <>
                        <div className="user-panel-sidebar-top">
                            <TbUserHeart className="user-icon" />
                            <span className="user-name">{entityName || '\u00A0'}</span>
                        </div>
                        <ul className="user-panel-sidebar-menu">
                            <li
                                className={`menu-item ${activePage === 'AccountDetails' ? 'active' : ''}`}
                                onClick={() => handleNavigation('AccountDetails')}
                            >
                                <TbUserEdit className="menu-icon" />
                                <span>Account Details</span>
                            </li>
                            <li
                                className={`menu-item ${activePage === 'EntityDetails' ? 'active' : ''}`}
                                onClick={() => handleNavigation('EntityDetails')}
                            >
                                <BiSolidBuildingHouse className="menu-icon" />
                                <span>Entity Details</span>
                            </li>
                            {showAdminOptions && (
                                <>
                                    <li
                                        className={`menu-item ${activePage === 'AddNewAccount' ? 'active' : ''}`}
                                        onClick={() => handleNavigation('AddNewAccount')}
                                    >
                                        <RiUserAddLine className="menu-icon" />
                                        <span>Add New Account</span>
                                    </li>
                                    <li
                                        className={`menu-item ${activePage === 'AddNewEntity' ? 'active' : ''}`}
                                        onClick={() => handleNavigation('AddNewEntity')}
                                    >
                                        <MdDomainAdd className="menu-icon" />
                                        <span>Add New Entity</span>
                                    </li>
                                </>
                            )}
                            <li className="menu-item" onClick={handleLogout}>
                                <FiLogOut className="menu-icon" />
                                <span>Log Out</span>
                            </li>
                        </ul>
                    </>
                )}
            </div>

            {/* Overlay to close sidebar when clicked outside on small screens */}
            {isSidebarOpen && <div className="sidebar-overlay" onClick={toggleSidebar}></div>}
        </div>
    );
};

export default UserPanelSidebar;
